/**
 * useApiQuery / useApiMutation — thin wrappers around WardkeyClient calls.
 *
 * Intentionally light: no caching, no stale-while-revalidate, no global
 * store. Each hook is a small state machine. If we ever want background
 * refetch or shared cache we can swap to React Query without changing
 * call sites — the return shape mirrors theirs.
 */

import { WardkeyAPIError, type WardkeyClient } from "@wardkey/sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "../toast";
import { useApiClient } from "./client";

export type ApiError = WardkeyAPIError | Error;

export type UseApiQueryOptions<T> = {
  /**
   * When `false`, the query is dormant — no fetch fires, `data` stays
   * undefined, `isLoading` is `false`. Flipping to `true` triggers the
   * fetch. Used to gate queries on an active org id or similar.
   */
  enabled?: boolean;
  /**
   * Suppress the automatic error toast — useful when a route renders its
   * own inline error UI and doesn't want a duplicate.
   */
  suppressToast?: boolean;
  /**
   * Called on success — handy for routes that want to derive secondary
   * state (e.g. seed a form from the fetched record).
   */
  onSuccess?: (data: T) => void;
};

export type UseApiQueryResult<T> = {
  data: T | undefined;
  error: ApiError | null;
  isLoading: boolean;
  /** Re-run the fetch. Resets error/loading. */
  refetch: () => void;
};

/**
 * Fire a one-off read against the API. Re-runs when any element of `key`
 * changes by `Object.is` semantics — pass the org id, path, filter inputs.
 * The `key` array is the dependency array; like React's `useEffect`,
 * stable identity matters.
 */
export function useApiQuery<T>(
  key: ReadonlyArray<unknown>,
  fn: (client: WardkeyClient, signal: AbortSignal) => Promise<T>,
  opts: UseApiQueryOptions<T> = {},
): UseApiQueryResult<T> {
  const client = useApiClient();
  const { pushToast } = useToast();
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<ApiError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [nonce, setNonce] = useState(0);

  // Stash the latest fn/opts in refs so the effect dep array is just `key`,
  // preventing identity churn from triggering re-fetches.
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const enabled = opts.enabled !== false;

  // biome-ignore lint/correctness/useExhaustiveDependencies: key is the dep array
  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    const ctrl = new AbortController();
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    fnRef
      .current(client, ctrl.signal)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setIsLoading(false);
        optsRef.current.onSuccess?.(result);
      })
      .catch((e: unknown) => {
        if (cancelled || ctrl.signal.aborted) return;
        const wrapped = wrapError(e);
        setError(wrapped);
        setIsLoading(false);
        if (!optsRef.current.suppressToast) {
          surfaceError(wrapped, pushToast);
        }
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, enabled, nonce, ...key]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return useMemo(() => ({ data, error, isLoading, refetch }), [data, error, isLoading, refetch]);
}

export type UseApiMutationResult<TArgs, TResult> = {
  mutate: (args: TArgs) => void;
  mutateAsync: (args: TArgs) => Promise<TResult>;
  data: TResult | undefined;
  error: ApiError | null;
  isPending: boolean;
  reset: () => void;
};

export type UseApiMutationOptions<TArgs, TResult> = {
  onSuccess?: (data: TResult, args: TArgs) => void;
  onError?: (error: ApiError, args: TArgs) => void;
  /** Suppress the automatic error toast (call site handles errors inline). */
  suppressToast?: boolean;
};

/**
 * Fire a write against the API. Returns both an imperative `mutateAsync`
 * (for awaitable flows like "submit form, then navigate") and a fire-and-
 * forget `mutate` (for clickhandlers that just want a state update).
 */
export function useApiMutation<TArgs, TResult>(
  fn: (client: WardkeyClient, args: TArgs) => Promise<TResult>,
  opts: UseApiMutationOptions<TArgs, TResult> = {},
): UseApiMutationResult<TArgs, TResult> {
  const client = useApiClient();
  const { pushToast } = useToast();
  const [data, setData] = useState<TResult | undefined>(undefined);
  const [error, setError] = useState<ApiError | null>(null);
  const [isPending, setIsPending] = useState(false);

  const optsRef = useRef(opts);
  optsRef.current = opts;

  const mutateAsync = useCallback(
    async (args: TArgs): Promise<TResult> => {
      setIsPending(true);
      setError(null);
      try {
        const result = await fn(client, args);
        setData(result);
        setIsPending(false);
        optsRef.current.onSuccess?.(result, args);
        return result;
      } catch (e: unknown) {
        const wrapped = wrapError(e);
        setError(wrapped);
        setIsPending(false);
        optsRef.current.onError?.(wrapped, args);
        if (!optsRef.current.suppressToast) {
          surfaceError(wrapped, pushToast);
        }
        throw wrapped;
      }
    },
    [client, fn, pushToast],
  );

  const mutate = useCallback(
    (args: TArgs) => {
      // Swallow rejection — call sites that want it should use mutateAsync.
      mutateAsync(args).catch(() => {});
    },
    [mutateAsync],
  );

  const reset = useCallback(() => {
    setData(undefined);
    setError(null);
    setIsPending(false);
  }, []);

  return useMemo(
    () => ({ mutate, mutateAsync, data, error, isPending, reset }),
    [mutate, mutateAsync, data, error, isPending, reset],
  );
}

function wrapError(e: unknown): ApiError {
  if (e instanceof WardkeyAPIError) return e;
  if (e instanceof Error) return e;
  return new Error(String(e));
}

function surfaceError(err: ApiError, pushToast: ReturnType<typeof useToast>["pushToast"]): void {
  if (err instanceof WardkeyAPIError) {
    const title = err.message || err.code || "Request failed";
    if (err.requestId) {
      pushToast({ kind: "error", title, body: `request id: ${err.requestId}` });
    } else {
      pushToast({ kind: "error", title });
    }
    return;
  }
  // AbortError fires when a route unmounts mid-fetch — never surfaced.
  if (err.name === "AbortError") return;
  pushToast({ kind: "error", title: err.message || "Request failed" });
}
